//////////////////////////////////////////////////////////////////////////////
//
//  사용자 잔액 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-21
//
//  설명: 사용자 포인트 잔액 목록 조회, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface PointBalanceListState {
    balances: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_balance_list_page_size";

class PointBalanceListScreenClass extends Component<any, PointBalanceListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            balances: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
        };
    }

    componentDidMount() {
        this.loadBalances();
    }

    loadBalances = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
            };

            const res = await request("PointBalance.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const balances = (pagedList.items || []).map((balance: any) => {
                    const lastTransactionDt = balance.lastTransactionDt || null;

                    return {
                        id: balance.seqId,
                        userName: balance.userName || "-",
                        email: balance.email || "-",
                        totalEarned: balance.totalEarned?.toLocaleString() || "0",
                        totalUsed: balance.totalUsed?.toLocaleString() || "0",
                        currentBalance: balance.currentBalance?.toLocaleString() || "0",
                        lastTransactionDt,
                    };
                });

                this.setState({
                    balances,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("포인트 잔액 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadBalances();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadBalances();
        });
    };

    render() {
        const { balances, pageSize, loading, totalCount, pageNumber } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2} textAlign="center">
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            { Header: "사용자명", accessor: "userName", align: "left", width: "15%" },
            { Header: "이메일", accessor: "email", align: "left", width: "20%" },
            {
                Header: "총 적립",
                accessor: "totalEarned",
                align: "right",
                width: "12%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="h6" fontWeight="bold" color="success">
                        {row.original.totalEarned}
                    </MDTypography>
                )
            },
            {
                Header: "총 사용",
                accessor: "totalUsed",
                align: "right",
                width: "12%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="h6" fontWeight="bold" color="error">
                        {row.original.totalUsed}
                    </MDTypography>
                )
            },
            {
                Header: "현재 잔액",
                accessor: "currentBalance",
                align: "right",
                width: "13%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="h5" fontWeight="bold" color="info">
                        {row.original.currentBalance}
                    </MDTypography>
                )
            },
            {
                Header: "마지막 거래일시",
                accessor: "lastTransactionDt",
                align: "center",
                width: "18%",
                Cell: ({ row }: any) => renderDateCell(row.original.lastTransactionDt)
            },
        ];

        const rows = balances;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>account_balance_wallet</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        사용자 잔액 목록 ( USER BALANCES )
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                사용자 포인트 잔액 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={false}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }
}

function PointBalanceListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <PointBalanceListScreenClass {...props} darkMode={darkMode} />;
}

export default PointBalanceListScreen;
